import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowRight, Users, Workflow, BarChart3, Compass, PenLine } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const HELP_AREAS = [
  {
    icon: Users,
    title: "HR & People Systems",
    text: "We help businesses structure and improve HR processes, recruitment workflows, documentation and people-management systems.",
  },
  {
    icon: Workflow,
    title: "Business Process Consulting",
    text: "We examine how work is currently being done, identify gaps and opportunities, and help create practical systems that make operations more organised and sustainable.",
  },
  {
    icon: BarChart3,
    title: "Business Analysis & Data",
    text: "We help turn business information into structured insights that support better planning, decision-making and performance management.",
  },
  {
    icon: Compass,
    title: "Brand & Digital Coordination",
    text: "We help businesses bring greater consistency across their website, digital platforms, content, communication and other customer-facing touchpoints.",
  },
  {
    icon: PenLine,
    title: "Content & Digital Support",
    text: "Where required, we help businesses organise and repurpose content across relevant digital channels — with a focus on consistency, clarity and purposeful communication.",
  },
];

const CHAIN = ["People", "Processes", "Data", "Communication", "Growth"];

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="about-page" className="bg-[#FAF5EC] min-h-screen text-kiki-ink warm-spotlight">
      <header className="border-b border-kiki-line bg-[#FAF5EC]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center">
          <Link data-testid="about-back-link" to="/" className="flex items-center gap-2.5 text-sm text-kiki-muted hover:text-kiki-gold transition-colors duration-300 font-outfit">
            <ArrowLeft className="w-4 h-4" />
            <img src="/logo.png" alt="Kiki Services" className="w-8 h-8 rounded-full object-cover border border-kiki-line" />
            <span className="font-display text-xl text-kiki-ink">Kiki<span className="italic text-kiki-gold">Services</span></span>
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <Reveal><Eyebrow>Our story</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-kiki-ink mt-3 leading-[1.05]">
            About Kiki <span className="italic text-kiki-gold">Services</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 font-display italic text-2xl text-kiki-gold font-light leading-snug">
            Building better businesses through people, processes, data and digital presence.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-6 space-y-4 text-lg text-kiki-muted font-light leading-relaxed">
            <p>
              Kiki Services is a consulting-led practice founded by Bhakti Narang, created to help businesses
              bring greater clarity, structure and consistency to the way they operate and grow.
            </p>
            <p>
              We work at the intersection of people, processes, business analysis and digital presence —
              helping organisations strengthen what happens behind the scenes while creating a clearer and
              more consistent presence in front of their customers.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-kiki-line pt-12">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-kiki-ink">From HR expertise to integrated business consulting</h2>
            <div className="mt-6 space-y-4 text-base text-kiki-muted font-light leading-relaxed">
              <p>
                Kiki Services has grown from a strong foundation in Human Resource Management and recruitment
                into a broader consulting practice.
              </p>
              <p>
                Our earlier work and experience through HRM Express remain an important part of that journey.
                Rather than leaving that experience behind, Kiki Services builds on it — bringing the same
                understanding of people and organisations into a wider business context.
              </p>
              <p>Today, our approach goes beyond individual HR activities. We look at how different parts of a business connect:</p>
            </div>
            <div data-testid="about-chain" className="mt-8 flex flex-wrap items-center gap-3">
              {CHAIN.map((c, i) => (
                <span key={c} className="flex items-center gap-3">
                  <span className="font-display text-xl sm:text-2xl text-kiki-ink bg-white border border-kiki-line rounded-full px-5 py-2">{c}</span>
                  {i < CHAIN.length - 1 && <ArrowRight className="w-4 h-4 text-kiki-gold" />}
                </span>
              ))}
            </div>
            <p className="mt-8 text-base text-kiki-muted font-light leading-relaxed">
              When these elements work together, businesses can operate with greater clarity, make better
              decisions and create more consistent experiences for their teams and customers.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-kiki-line pt-12">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-kiki-ink">What we help businesses with</h2>
            <p className="mt-4 text-base text-kiki-muted font-light">Our work can span different areas depending on the needs of the organisation.</p>
            <div className="mt-8 space-y-4">
              {HELP_AREAS.map((a, i) => (
                <div key={a.title} data-testid={`about-area-${i}`} className="flex items-start gap-4 rounded-2xl border border-kiki-line bg-white p-6">
                  <span className="w-11 h-11 rounded-full bg-kiki-surface flex items-center justify-center text-kiki-gold shrink-0">
                    <a.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-outfit text-lg font-medium text-kiki-ink">{a.title}</h3>
                    <p className="text-sm text-kiki-muted font-light leading-relaxed mt-1">{a.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-kiki-line pt-12">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-kiki-ink">Our approach</h2>
            <div className="mt-6 space-y-4 text-base text-kiki-muted font-light leading-relaxed">
              <p>We do not believe in introducing complexity simply for the sake of appearing sophisticated.</p>
              <p className="font-display text-xl text-kiki-ink">Our approach is practical.</p>
              <p>
                We first understand how the business works, where the gaps are and what the organisation is
                actually trying to achieve. From there, we help create systems and processes that are
                appropriate for its current stage while allowing room for future growth.
              </p>
              <p>
                We believe good consulting should leave a business more organised, more informed and more
                capable than it was before.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-kiki-line pt-12">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-kiki-ink">Technology with a human approach</h2>
            <div className="mt-6 space-y-4 text-base text-kiki-muted font-light leading-relaxed">
              <p>Technology and AI are changing the way businesses work.</p>
              <p>
                We believe they should be used thoughtfully — to reduce repetitive work, improve organisation,
                support analysis and create efficiencies — while keeping human judgement at the centre of
                important decisions.
              </p>
              <p>For us, automation is not about removing the human element.</p>
              <p className="font-display text-xl text-kiki-ink">
                It is about giving people more time to focus on the work that actually requires them.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-kiki-line pt-12">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-kiki-ink">Who we work with</h2>
            <div className="mt-6 space-y-4 text-base text-kiki-muted font-light leading-relaxed">
              <p>
                Kiki Services works with startups, SMEs, founders and growing businesses that want to build
                stronger foundations and create more organised ways of working.
              </p>
              <p>Some businesses need help creating systems from the beginning.</p>
              <p>Others already have systems but need to simplify, connect or improve them.</p>
              <p>And some need help bringing their internal operations and external digital presence into better alignment.</p>
              <p>The starting point may be different, but the objective remains the same:</p>
              <p className="font-display text-xl text-kiki-gold italic">
                Create clarity. Build structure. Improve consistency. Enable growth.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 border-t border-kiki-line pt-12">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-kiki-ink">Why Kiki Services?</h2>
            <div className="mt-6 space-y-4 text-base text-kiki-muted font-light leading-relaxed">
              <p>Because businesses rarely have problems that exist in only one department.</p>
              <div className="border-l-2 border-kiki-gold pl-6 space-y-2 font-display text-lg text-kiki-ink">
                <p>A recruitment problem can be a process problem.</p>
                <p>A process problem can become a data problem.</p>
                <p>A data problem can affect decision-making.</p>
                <p>And inconsistent communication can affect how the entire business is perceived.</p>
              </div>
              <p>We therefore look at the business as a connected system rather than a collection of isolated tasks.</p>
              <p>Our goal is to help businesses create that connection.</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 rounded-2xl border border-kiki-line bg-white p-8 sm:p-12 text-center">
            <Eyebrow>Our belief</Eyebrow>
            <blockquote data-testid="about-belief" className="mt-4 font-display text-2xl sm:text-4xl font-light text-kiki-ink leading-snug">
              “Strong businesses are built when people, processes, data and communication work in sync.”
            </blockquote>
            <p className="mt-6 text-base text-kiki-muted font-light leading-relaxed max-w-2xl mx-auto">
              Kiki Services exists to help make that possible — through practical consulting, thoughtful
              systems and solutions designed around the business rather than around a one-size-fits-all formula.
            </p>
            <div className="mt-8 pt-8 border-t border-kiki-line">
              <p className="font-display text-2xl text-kiki-ink">Kiki<span className="italic text-kiki-gold">Services</span></p>
              <p className="text-sm text-kiki-muted font-light mt-2">Consulting for better ways of working.</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-kiki-gold mt-3">People · Processes · Data · Digital</p>
              <p className="text-sm text-kiki-muted font-light mt-4">Founded by Bhakti Narang</p>
              <p className="text-sm text-kiki-muted font-light mt-2">
                <a href="mailto:bhaktinarang@kikiservices.co.in" className="text-kiki-gold underline">bhaktinarang@kikiservices.co.in</a>
                {" · "}+91 98679 82112
              </p>
            </div>
            <Link
              data-testid="about-book-btn"
              to="/book"
              className="mt-8 inline-flex items-center gap-2 bg-kiki-ink hover:bg-kiki-gold text-[#FAF5EC] font-outfit font-semibold rounded-full px-8 py-4 transition-colors duration-300"
            >
              Work with us <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default AboutPage;
